import api from '../../services/api';
import { ResponseData } from '../../types/response/ResponseData';
import { UserGroup } from '../../types/UserGroup';
import { PostResponseData } from '../../types/response/PostResponseData';
import { User } from '../../types/users/User';
import { PatchOperation } from '../../types/requests/PatchOperation';
import ResponseError from '../../types/response/ResponseError';

export const listGroups = async (
    organizationId: string,
    groupSearchStarted: (phrase: string) => void,
    groupSearchSucceeded: (results: ResponseData<UserGroup[]>) => void,
    groupSearchFailed: (error: ResponseError) => void
) => {
    groupSearchStarted(organizationId);

    if (!organizationId) {
        return;
    }

    try {
        const response = await api.get<ResponseData<UserGroup[]>>(
            `organizations/${organizationId}/groups`
        );

        groupSearchSucceeded(response);
    } catch (error) {
        groupSearchFailed(error as ResponseError);
    }
};

export const getGroup = async (
    organizationId: string,
    groupId: string,
    getGroupDetailsStarted: () => void,
    getGroupDetailsSucceeded: (group: UserGroup) => void,
    getGroupDetailsFailed: () => void
) => {
    getGroupDetailsStarted();

    try {
        const response = await api.get<ResponseData<UserGroup>>(
            `/organizations/${organizationId}/groups/${groupId}`
        );
        getGroupDetailsSucceeded(response.data);
    } catch {
        getGroupDetailsFailed();
    }
};

export const getOrganizationPermissions = async (
    organization: string,
    getOrganizationPermissionsStarted: () => void,
    getOrganizationPermissionsSucceeded: (permissions: Record<string, string[]>) => void,
    getOrganizationPermissionsFailed: () => void
) => {
    getOrganizationPermissionsStarted();

    if (!organization) {
        return;
    }

    try {
        const response = await api.get<ResponseData<Record<string, string[]>>>(
            `/organizations/${organization}/permissions`
        );
        getOrganizationPermissionsSucceeded(response.data);
    } catch {
        getOrganizationPermissionsFailed();
    }
};

export const toggleGroupPermissions = async (
    organizationId: string,
    permissions: string[],
    operation: string,
    isChecked: boolean,
    groupId: string,
    availablePermissions: Record<string, string[]>,
    currentPermissions: string[],
    groupPermissionsToggleStarted: (payload: PatchOperation[]) => void,
    groupPermissionsToggleSucceeded: (response: ResponseData<UserGroup>) => void,
    patchGroupFailed: (error: ResponseError) => void
) => {
    const operations = permissions
        .filter((p: string) => {
            if (availablePermissions && availablePermissions[p])
                return availablePermissions[p].includes(operation);
            return false;
        })
        .reduce((result: PatchOperation[], p: string) => {
            const index = currentPermissions.indexOf(`${operation}-${p}`);
            if (isChecked && index === -1) {
                result.push({
                    op: 'add',
                    path: '/permissions/-',
                    value: `${operation}-${p}`,
                });
            }

            if (index > -1) {
                result.push({
                    op: 'remove',
                    path: `/permissions/${index}`,
                    value: undefined,
                });
            }

            return result;
        }, [] as PatchOperation[])
        .sort((a, b) => {
            const numberA = a.path.substring(a.path.lastIndexOf('/') + 1);
            const numberB = b.path.substring(b.path.lastIndexOf('/') + 1);

            return Number(numberB) - Number(numberA);
        });

    groupPermissionsToggleStarted(operations);
    try {
        const response = (await api.patch<PatchOperation[]>(
            `/organizations/${organizationId}/groups/${groupId}`,
            operations
        )) as ResponseData<UserGroup>;

        groupPermissionsToggleSucceeded(response);
    } catch (error) {
        patchGroupFailed(error as ResponseError);
    }
};

export const toggleStore = async (
    organizationId: string,
    storeId: string,
    isChecked: boolean,
    currentStores: string[],
    groupId: string,
    groupStoreAdded: (store: string) => void,
    groupStoreRemoved: (store: string) => void,
    patchGroupFailed: (error: ResponseError) => void
) => {
    const index = (currentStores || []).indexOf(storeId);
    const operations = [] as PatchOperation[];

    if (isChecked) {
        if (index === -1) {
            groupStoreAdded(storeId);
            operations.push({ op: 'add', path: '/connectedStores/-', value: storeId });
        }
    } else if (index > -1) {
        groupStoreRemoved(storeId);
        operations.push({ op: 'remove', path: `/connectedStores/${index}`, value: undefined });
    }

    try {
        await api.patch<PatchOperation[]>(
            `/organizations/${organizationId}/groups/${groupId}`,
            operations
        );
    } catch (error) {
        patchGroupFailed(error as ResponseError);
    }
};

export const deleteGroup = async (
    organization: string | undefined,
    groupId: string,
    deleteGroupStarted: () => void,
    deleteGroupSucceeded: (groupId: string) => void,
    deleteGroupFailed: (error: ResponseError) => void
) => {
    deleteGroupStarted();

    try {
        await api.del(`organizations/${organization}/groups/${groupId}`);
        deleteGroupSucceeded(groupId);
    } catch (error) {
        deleteGroupFailed(error as ResponseError);
    }
};

export const createGroup = async (
    organizationId: string,
    name: string,
    createGroupStarted: (name: string) => void,
    createGroupSucceeded: (response: PostResponseData) => void,
    createGroupFailed: (error: ResponseError) => void,
    groupSearchStarted: (phrase: string) => void,
    groupSearchSucceeded: (results: ResponseData<UserGroup[]>) => void,
    groupSearchFailed: (error: ResponseError) => void
) => {
    createGroupStarted(name);

    try {
        const result = await api.post<{ name: string }>(`organizations/${organizationId}/groups/`, {
            name,
        });
        createGroupSucceeded(result);
        await listGroups(
            organizationId || '',
            groupSearchStarted,
            groupSearchSucceeded,
            groupSearchFailed
        );
    } catch (error) {
        createGroupFailed(error as ResponseError);
    }
};

export const listGroupUsers = async (
    organizationId: string,
    groupId: string,
    getGroupListUsersStarted: (group: { organization: string; groupId: string }) => void,
    getGroupListUsersSucceeded: (groupListUsersResponse: ResponseData<User[]>) => void,
    getGroupListUsersFailed: (error: ResponseError) => void
) => {
    getGroupListUsersStarted({ organization: organizationId, groupId });

    try {
        const result = await api.get<ResponseData<User[]>>(
            `/organizations/${organizationId}/groups/${groupId}/users`
        );
        getGroupListUsersSucceeded(result);
    } catch (error) {
        getGroupListUsersFailed(error as ResponseError);
    }
};

export const addUserToGroup = async (
    organization: string,
    userId: string,
    group: string,
    users: User[],
    addUserToGroupStarted: (data: {
        groupId: string;
        userId: string;
        organizationId: string;
    }) => void,
    addUserToGroupSucceeded: (data: { response: PostResponseData; user: User }) => void,
    addUserToGroupFailed: (error: ResponseError) => void,
) => {
    const user =
        users.find(usr => {
            return usr.id === userId;
        }) || ({} as User);
    addUserToGroupStarted({ organizationId: organization, userId, groupId: group });
    try {
        const response = await api.post<{ group: string }>(
            `organizations/${organization}/users/${userId}/groups`,
            {
                group,
            }
        );
        addUserToGroupSucceeded({ response, user });
    } catch (error) {
        addUserToGroupFailed(error as ResponseError);
    }
};

export const removeUserFromGroup = async (
    organization: string,
    userId: string,
    groupId: string,
    removeUserFromGroupStarted: (data: {
        groupId: string;
        userId: string;
        organizationId: string;
    }) => void,
    removeUserFromGroupSucceeded: () => void,
    removeUserFromGroupFailed: (error: ResponseError) => void
) => {
    removeUserFromGroupStarted({ organizationId: organization, userId, groupId });

    try {
        await api.del(`organizations/${organization}/users/${userId}/groups/${groupId}`);
        removeUserFromGroupSucceeded();
    } catch (error) {
        removeUserFromGroupFailed(error as ResponseError);
    }
};
